import React, { useState, useEffect, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { getAllPatientsWithQueueDetails } from '../../clientServices/PatientsService';
import FinishTreatmentModal from './modals/DeletePatient'; // Adjust the import as needed
import BasicModal from './modals/EmergencyDoctorAlert';
import useReceptionSocket from '../../clientServices/ReceptionSocket';
import './PatientAndQueueDetails.css';


const PatientAndQueueDetails = ({ setSelectedPatient, patients, setPatients }) => {
    const [queues, setQueues] = useState([]);
    const [value, setValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [noResults, setNoResults] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false); // State for emergency modal open/close
    const [selectedPatientName, setSelectedPatientName] = useState('');
    const [selectedRoomName, setSelectedRoomName] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { emergencyAlertToDoctor, endOfTreatment ,patientChange } = useReceptionSocket(setSelectedPatient, patients, setPatients);

    useEffect(() => {
        const fetchQueueDetails = async () => {
            setLoading(true);
            try {
         

                const response = await getAllPatientsWithQueueDetails();
                const patients = response.data?.data?.filter(item => item.Tz !== null) || []; // מוסיפים בדיקה אם הנתונים קיימים
               

                const groupedQueues = {};
                console.log(patients)
                if (patients) {

                    patients.forEach((patient) => {
                        patient.queues.forEach((queue) => {
                            if (!groupedQueues[queue.RoomId]) {
                                groupedQueues[queue.RoomId] = {
                                    roomId: queue.RoomId,
                                    roomName: queue.room.Name,
                                    patients: [],
                                };
                            }
                            if (patient.UniqueNumber)
                                groupedQueues[queue.RoomId].patients.push({
                                    ...patient,
                                    queuePriority: queue.PriorityNumber,
                                });
                        });
                    });

                    // Sort patients within each queue and assign PriorityNumber
                    Object.keys(groupedQueues).forEach(queueId => {
                        groupedQueues[queueId].patients.sort((a, b) => a.queuePriority - b.queuePriority);

                        groupedQueues[queueId].patients.forEach((patient, index) => {
                            const queue = patient.queues.find(q => q.RoomId === parseInt(queueId));
                            if (queue) {
                                queue.PriorityNumber = index + 1; // Assign PriorityNumber based on sorted order
                            }
                        });
                    });

                    setQueues(Object.values(groupedQueues));
                }



            } catch (error) {
                console.error('Error fetching queue details:', error);
                setNoResults(true); // Handle error state as needed
            } finally {
                setLoading(false);
            }
        };

        fetchQueueDetails();
    }, [patientChange]);

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
    };

    const filteredPatients = useMemo(() => {
        return (
            queues[value]?.patients.filter(patient =>
                (patient.FirstName && patient.FirstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (patient.LastName && patient.LastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (patient.UniqueNumber && patient.UniqueNumber.toString().includes(searchTerm)) ||
                (patient.Tz && String(patient.Tz).includes(searchTerm)) ||
                (patient.HMO && patient.HMO.Name.toLowerCase().includes(searchTerm.toLowerCase()))
            ) || []
        );
    }, [queues, value, searchTerm]);


    useEffect(() => {
        if (searchTerm && filteredPatients.length === 0) {
            setNoResults(true);
        } else {
            setNoResults(false);
        }
    }, [searchTerm, filteredPatients]);

    const handleRowClick = (patientId) => {
        setSelectedPatientId(patientId);
    };

    const handleOpenEmergencyModal = (patient) => {
        setSelectedPatientName(patient.UniqueNumber);
        setSelectedRoomName(queues[value].roomName);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleConfirm = () => {
        emergencyAlertToDoctor(selectedPatient.UniqueNumber, selectedRoomName);
        handleCloseModal();
    };

    const handleFinishTreatment = (patient) => {
        setSelectedPatient(patient); // Set the selected patient
        setIsDeleteModalOpen(true); // Open the Finish Treatment modal
    };

    const handleConfirmDeleteModal = async () => {
        try {
            await endOfTreatment(selectedPatient.ID);
            alert('הטיפול הסתיים בהצלחה');
            setSelectedPatient(null);
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting patient:', error);
            alert('Failed to delete patient.');
        }
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const selectedPatient = filteredPatients.find(patient => patient.ID === selectedPatientId);

    return (
        <Paper sx={{ padding: 2 }}>

            {loading ? (
                <div>Loading...</div>
            ) : (


                <div>
                    <Tabs value={value} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        {queues.map((queue) => (
                            <Tab key={queue.roomId} label={queue.roomName} />
                        ))}
                    </Tabs>

                    <TextField
                        label="חיפוש"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <Paper sx={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 2 }}>
                        {queues.length > 0 ? (
                            <div>
                                {noResults ? (
                                    <div>לא נמצאו תוצאות עבור החיפוש שלך</div>
                                ) : (
                                    <div>
                                        {queues.map((queue, index) => (
                                            <div role="tabpanel" hidden={value !== index} key={queue.roomId}>
                                                {value === index && (
                                                    filteredPatients.length > 0 ? (
                                                        <TableContainer component={Paper}>
                                                            <Table sx={{ minWidth: 900 }} aria-label="simple table">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell align="center"><strong>מיקום בתור</strong></TableCell>
                                                                        <TableCell align="center"><strong>מספר יחודי</strong></TableCell>
                                                                        <TableCell align="center"><strong>קופת חולים</strong></TableCell>
                                                                        <TableCell align="center"><strong>מספר זהות</strong></TableCell>
                                                                        <TableCell align="center"><strong>שעת כניסה</strong></TableCell>
                                                                        <TableCell align="center"><strong>פעולה</strong></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {filteredPatients.map((patient) => (
                                                                        <TableRow
                                                                            key={patient.ID}
                                                                            onClick={() => handleRowClick(patient.ID)}
                                                                            sx={{
                                                                                backgroundColor: selectedPatientId === patient.ID ? '#e0e0e0' : 'inherit',
                                                                                cursor: 'pointer',
                                                                            }}
                                                                        >
                                                                            <TableCell align="center">{patient.queues[0].PriorityNumber}</TableCell>
                                                                            <TableCell align="center">{patient.UniqueNumber}</TableCell>
                                                                            <TableCell align="center">{patient.HMO.Name}</TableCell>
                                                                            <TableCell align="center">{patient.Tz}</TableCell>
                                                                            <TableCell align="center">{patient.CheckIn ? new Date(patient.CheckIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}</TableCell>
                                                                            <TableCell align="center">
                                                                                <div className='detailsTableButtons'>
                                                                                    <button className='callDoctor' onClick={() => handleOpenEmergencyModal(patient)}>קריאה לרופא</button>
                                                                                    <button className='finishTreatment' onClick={() => handleFinishTreatment(patient)}>סיום טיפול</button>
                                                                                </div>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    ) : (
                                                        <div> אין נתונים</div>
                                                    )
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className='noData'> אין נתונים</div>
                        )}
                    </Paper>


                </div>
            )}

            {/* BasicModal for emergency alert */}
            <BasicModal
                open={modalOpen}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirm}
                patientName={selectedPatientName}
                roomName={selectedRoomName}
            />

            {/* Finish Treatment Modal */}
            <FinishTreatmentModal
                open={isDeleteModalOpen}
                handleClose={handleCloseDeleteModal}
                handleConfirm={handleConfirmDeleteModal}
                patientName={selectedPatient ? `${selectedPatient.UniqueNumber}` : ''}
            />
        </Paper>
    );
};

export default PatientAndQueueDetails;
